/** @format */

import React, { useState } from "react";
import { Book } from "../types";
import { formatCurrency } from "../utils/formatUtils";
import { generateBookAffiliateLink, generateAffiliateText } from "../utils/formatUtils";
import { shouldShowIOSWebViewAlert } from "../utils/deviceDetection";
import { useShare } from "../hooks/useShare";
import { useAuth } from "../contexts/AuthContext";
import { useFavorites } from "../hooks/useFavorites";
import {
  BookOpenIcon,
  ShoppingCartIcon,
  StarIcon,
  EyeIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  ShareIcon,
  UserIcon,
  ClockIcon,
  TagIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import {
  StarIcon as StarIconSolid,
  HeartIcon as HeartIconSolid,
} from "@heroicons/react/24/solid";

interface BookCardProps {
  book: Book;
  onViewDetails: () => void;
  onViewReviews: () => void;
  onAddToCart?: (book: Book) => void;
  showAddToCart?: boolean;
  layout?: 'vertical' | 'horizontal';
}

const BookCard: React.FC<BookCardProps> = ({
  book,
  onViewDetails,
  onViewReviews,
  onAddToCart,
  showAddToCart = true,
  layout = 'vertical',
}) => {
  const { handleShare } = useShare();
  const { currentUser } = useAuth();
  const { isBookFavorited, toggleFavorite } = useFavorites();
  const [showFullImage, setShowFullImage] = useState(false);
  const [showHelperText, setShowHelperText] = useState(false);

  const renderStars = (rating: number | undefined) => {
    if (!rating) return null;
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star}>
            {rating >= star ? (
              <StarIconSolid className="h-4 w-4 text-yellow-400 drop-shadow-sm" />
            ) : (
              <StarIcon className="h-4 w-4 text-yellow-300" />
            )}
          </span>
        ))}
        <span className="text-sm text-gray-600 ml-1 font-medium">
          ({rating})
        </span>
      </div>
    );
  };

  const getProgramColor = (program: "ICAG" | "CITG" | "general") => {
    switch (program) {
      case "ICAG":
        return "bg-gradient-to-r from-blue-500 to-indigo-500";
      case "CITG":
        return "bg-gradient-to-r from-green-500 to-emerald-500";
      default:
        return "bg-gradient-to-r from-gray-500 to-gray-600";
    }
  };

  // Share functionality with affiliate code
  const handleShareClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when sharing

    const bookUrl = generateBookAffiliateLink(book.id, currentUser?.referralCode || "");
    const shareText = generateAffiliateText(book.title, currentUser?.referralCode || "");

    await handleShare({
      title: book.title,
      text: shareText,
      url: bookUrl,
    });
  };

  // Add to cart or open directly if free
  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (book.amount === 0 && book.pdf_url) {
      // Open free book directly via MyBooks deep-link
      const openId = encodeURIComponent(book.id);
      const openTitle = encodeURIComponent(book.title);
      const openUrl = encodeURIComponent(book.pdf_url);
      const returnTo = encodeURIComponent('/app/books');
      window.location.href = `/app/books?openId=${openId}&openTitle=${openTitle}&openUrl=${openUrl}&returnTo=${returnTo}`;
      return;
    }
    if (onAddToCart) {
      onAddToCart(book);
    }
  };

  // Toggle favorite functionality
  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when toggling favorite

    if (!currentUser) {
      alert("Please sign in to add favorites");
      return;
    }
    try {
      await toggleFavorite(book);
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const isHorizontal = layout === 'horizontal';

  return (
    <>
      <div className="group transform hover:scale-[1.02] transition-all duration-300">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden relative h-full">
          {/* Book Cover */}
          <div className="relative w-full h-60 bg-gray-100 overflow-hidden">
            {book.coverImage ? (
              <img
                src={book.coverImage}
                alt={book.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpenIcon className="h-16 w-16 text-gray-400" />
              </div>
            )}

            {/* Expand Image Icon */}
            {book.coverImage && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowFullImage(true);
                }}
                className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gray-700 hover:text-blue-600 p-1.5 rounded-full shadow-sm transition-all duration-200 opacity-0 group-hover:opacity-100"
                title="View full image"
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
              </button>
            )}

            {/* Rating Badge */}
            {book.rating && book.rating > 0 && (
              <div className="absolute top-2 left-2">
                <div className="bg-white px-2 py-1 rounded text-gray-800 flex items-center shadow-sm">
                  <span className="text-xs font-bold text-blue-600">{book.rating.toFixed(1)}</span>
                </div>
              </div>
            )}

            {/* Price Badge - Hidden on iOS WebView */}
            {!shouldShowIOSWebViewAlert() && (
              <div className="absolute bottom-2 right-2">
                <span className={`px-2 py-1 text-xs font-bold rounded shadow-sm ${
                  book.amount === 0
                    ? "bg-green-600 text-white"
                    : "bg-slate-800 text-white"
                }`}>
                  {book.amount === 0 ? "FREE" : `₵${book.amount}`}
                </span>
              </div>
            )}
          </div>

          {/* Book Content */}
          <div className="p-3 flex-1 flex flex-col h-full">
            {/* Title */}
            <h4 className="font-bold text-gray-900 text-sm leading-tight mb-2 line-clamp-2 min-h-[2.5rem] group-hover:text-blue-600 transition-colors">
              {book.title}
            </h4>
            
            {/* Rating Display */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center">
                <span className="text-sm font-bold text-gray-900 mr-1">
                  {book.rating && book.rating > 0 ? book.rating.toFixed(1) : 'No ratings'}
                </span>
                <div className="flex items-center">
                  {[...Array(5)].map((_, index) => (
                    <svg
                      key={index}
                      className={`h-3 w-3 ${
                        book.rating && book.rating > 0 && index < Math.floor(book.rating)
                          ? 'text-yellow-400'
                          : book.rating && book.rating > 0 && index < book.rating
                          ? 'text-yellow-400'
                          : 'text-yellow-300'
                      }`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <span className="text-xs text-gray-500">
                {book.rating && book.rating > 0 ? `(${book.reviewCount || 0} reviews)` : '(Be the first to rate)'}
              </span>
            </div>
            
            {/* Tags Row */}
            <div className="flex flex-wrap items-center gap-1 mb-3">
              {/* Program Tag */}
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="truncate max-w-[80px]">
                  {book.program || 'General'}
                </span>
              </span>
              
              {/* Category Tag */}
              {book.category && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200">
                  <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM7 7a1 1 0 000 2h6a1 1 0 100-2H7zM7 11a1 1 0 000 2h6a1 1 0 100-2H7z" />
                  </svg>
                  <span className="truncate max-w-[80px]">
                    {book.category}
                  </span>
                </span>
              )}
              
              {/* Author Tag */}
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-50 text-purple-700 border border-purple-200">
                <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" />
                </svg>
                <span className="truncate max-w-[80px]">
                  by {book.author}
                </span>
              </span>
            </div>
            
            {/* Book Details */}
            <div className="mb-3 space-y-2">
              {/* Price Info - Hidden on iOS WebView */}
              {!shouldShowIOSWebViewAlert() && (
                <div className="flex items-center justify-between text-xs text-gray-600 p-1.5 bg-blue-50 rounded border border-blue-100">
                  <span className="flex items-center">
                    <svg className="h-3 w-3 mr-1.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                    </svg>
                    <span className="font-medium">Price</span>
                  </span>
                  <span className="font-medium text-blue-600">
                    {book.amount === 0 ? "FREE" : `₵${book.amount}`}
                  </span>
                </div>
              )}
              
              {/* Review Info */}
              <div className="text-xs text-gray-600 p-1.5 bg-green-50 rounded border border-green-100">
                <div className="flex items-center justify-between">
                  <span className="flex items-center">
                    <svg className="h-3 w-3 mr-1.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium text-gray-700">Reviews</span>
                  </span>
                  <span className="font-medium text-green-600">
                    {book.reviewCount || 0} review{book.reviewCount !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Button - Hidden on iOS WebView for paid books */}
            {!shouldShowIOSWebViewAlert() || book.amount === 0 ? (
              <div className="w-full relative z-10 mt-auto">
                <button
                  onClick={handleAddToCart}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold rounded transition-all duration-200"
                >
                  <ShoppingCartIcon className="h-4 w-4 mr-2" />
                  {book.amount === 0 ? "Read Now" : "Add to Cart"}
                </button>
              </div>
            ) : (
              /* For iOS WebView with paid books, show a simple "View Details" button */
              <div className="w-full relative z-10 mt-auto">
                <button
                  onClick={onViewDetails}
                  className="w-full inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white text-sm font-bold rounded transition-all duration-200"
                >
                  <BookOpenIcon className="h-4 w-4 mr-2" />
                  View Details
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full Image Modal */}
      {showFullImage && book.coverImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="relative max-w-4xl max-h-[90vh] w-full h-full">
            <img
              src={book.coverImage}
              alt={book.title}
              className="w-full h-full object-contain rounded-lg"
            />
            <button
              onClick={() => setShowFullImage(false)}
              className="absolute top-4 right-4 bg-white/90 hover:bg-white text-gray-700 hover:text-gray-900 p-2 rounded-full shadow-lg transition-all duration-200"
              title="Close"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BookCard;
